import { type ApiError, type components, isApiError, type operations } from '@multaqa/api-client'
import { queryOptions, useQueryClient } from '@tanstack/react-query'
import { api } from './api'

export type CurrentUser = components['schemas']['PlatformAdminResource']

type PlatformLoginResponse =
  operations['platform.login']['responses'][200]['content']['application/json']

type PlatformForgotPasswordRequest = components['schemas']['PlatformForgotPasswordRequest']
type PlatformResetPasswordRequest = components['schemas']['PlatformResetPasswordRequest']

/**
 * The authenticated user, as server state.
 *
 * `retry: false` matters: an unauthenticated visitor gets a 401, and that is a
 * definitive answer, not a transient failure. Retrying it delays every redirect
 * to the login page by the length of the backoff.
 */
export const currentUserQuery = queryOptions({
  queryKey: ['current-user'],
  retry: false,
  staleTime: 5 * 60_000,
  queryFn: async (): Promise<CurrentUser> => {
    const { data } = await api.GET('/platform/me')

    // The middleware in @multaqa/api-client throws on any non-ok response, so
    // a missing body here means the contract and the server disagree. Say so
    // rather than asserting non-null and failing later with a property access.
    if (!data) {
      throw new Error('GET /platform/me returned no body')
    }

    return data.data
  },
})

/**
 * Platform-admin login. Two-step by design: the password check does NOT sign
 * the admin in, it records a pending two-factor challenge and returns
 * `two_factor: true`. Only `verifyPlatformTwoFactor` completes the session.
 *
 * The plan spec is explicit that 2FA is mandatory for platform staff (FR-SEC-003)
 * — a platform admin who has not confirmed it cannot reach this branch at all.
 */
export async function platformLogin(input: {
  email: string
  password: string
}): Promise<PlatformLoginResponse> {
  const { data } = await api.POST('/platform/login', { body: input })
  if (!data) {
    throw new Error('POST /platform/login returned no body')
  }
  return data
}

/**
 * Submit the TOTP code the authenticator app produced. The session cookie
 * is set on the response — the next request to `/platform/me` resolves.
 */
export async function verifyPlatformTwoFactor(input: { code: string }): Promise<void> {
  await api.POST('/platform/two-factor', { body: input })
}

/**
 * Local-development door: signs straight into the demo platform admin,
 * bypassing the two-step FR-SEC-003 flow on purpose. The endpoint is guarded
 * by LocalOnly middleware, so this 204 only exists in development — the
 * button that calls it is compiled out of production builds.
 */
export async function devLogin(): Promise<void> {
  await api.POST('/platform/dev-login')
}

/**
 * Dev-login action, shaped like `usePlatformLogout`: a hook returning the
 * async action rather than a TanStack `useMutation`, so the caller wires its
 * own lifecycle. Signing in through the dev door sets the session cookie;
 * invalidating `['current-user']` makes the shell's `beforeLoad` guard see
 * the authenticated state on the very next render.
 */
export function usePlatformDevLogin() {
  const queryClient = useQueryClient()
  return async (): Promise<void> => {
    await devLogin()
    await queryClient.invalidateQueries({ queryKey: ['current-user'] })
  }
}

/**
 * Request a platform-admin password reset link. The endpoint always returns
 * 204 for a well-formed payload — even when the email does not name an
 * admin — so it cannot enumerate platform admins. The caller does not learn
 * whether the address is registered; the only signal is the absence of an
 * exception.
 */
export async function forgotPassword(input: PlatformForgotPasswordRequest): Promise<void> {
  await api.POST('/platform/forgot-password', { body: input })
}

/**
 * Redeem a platform-admin password reset link. 204 on success; 422 with a
 * `token` field error for an unknown, used or expired token. The
 * `password_confirmation` field is the standard Laravel convention — the
 * schema lives in `PlatformResetPasswordRequest`.
 */
export async function resetPassword(input: PlatformResetPasswordRequest): Promise<void> {
  await api.POST('/platform/reset-password', { body: input })
}

/**
 * Sign out the platform admin. The session is invalidated server-side; the
 * SPA also clears its query cache so a stale user object does not linger.
 */
export function usePlatformLogout() {
  const queryClient = useQueryClient()
  return async (): Promise<void> => {
    await api.POST('/platform/logout')
    // Drop every cached query. The next authenticated request will repopulate
    // them, and `currentUserQuery` will fail with 401 — which the api-client
    // middleware redirects away.
    queryClient.clear()
  }
}

/**
 * Surface the server's first field error on a given field, or fall back to a
 * generic root error. A 422 from Fortify and a 422 from the platform controller
 * both have the Laravel `errors: { field: [messages] }` shape, so this works
 * identically for both populations.
 */
export function firstFieldError(error: unknown, field: string): string | null {
  if (isApiError(error) && error.errors?.[field]?.[0]) {
    return error.errors[field][0] as string
  }
  return null
}

export type { ApiError }
